import { useCallback, useEffect, useRef, useState } from "react";
import { useMapsLibrary } from "@vis.gl/react-google-maps";
import { cn } from "@/lib/utils";
import { Label } from "@/components/ui/label";
import { MapPin, Loader2 } from "lucide-react";
import { RequiredMark } from "./required-mark";

interface GooglePlaceAutocompleteProps {
  onPlaceSelect: (place: google.maps.places.PlaceResult | null) => void;
  value?: string;
  onInputChange?: (value: string) => void;
  placeholder?: string;
  label?: string;
  countryRestrictions?: string[];
  disabled?: boolean;
  error?: string;
  className?: string;
  wrapperClassName?: string;
  required?: boolean;
}

/**
 * Google Places Autocomplete component built with shadcn styling.
 * Must be wrapped in an `APIProvider` from `@vis.gl/react-google-maps`.
 */
export const GooglePlaceAutocomplete = ({
  onPlaceSelect,
  value = "",
  onInputChange,
  placeholder,
  label,
  countryRestrictions,
  disabled = false,
  error,
  className,
  wrapperClassName,
  required = false,
}: GooglePlaceAutocompleteProps) => {
  const [inputValue, setInputValue] = useState(value);
  const [predictions, setPredictions] = useState<google.maps.places.AutocompletePrediction[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);

  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const autocompleteServiceRef = useRef<google.maps.places.AutocompleteService | null>(null);
  const placesServiceRef = useRef<google.maps.places.PlacesService | null>(null);
  const sessionTokenRef = useRef<google.maps.places.AutocompleteSessionToken | null>(null);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const places = useMapsLibrary("places");

  // Initialize Google Places services
  useEffect(() => {
    if (!places) return;
    autocompleteServiceRef.current = new places.AutocompleteService();
    placesServiceRef.current = new places.PlacesService(document.createElement("div"));
    sessionTokenRef.current = new places.AutocompleteSessionToken();
  }, [places]);

  // Sync with external value prop
  useEffect(() => {
    setInputValue(value);
  }, [value]);

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    };
  }, []);

  const fetchPredictions = useCallback(
    (input: string) => {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);

      if (!input.trim()) {
        setPredictions([]);
        setIsOpen(false);
        setIsLoading(false);
        return;
      }

      setIsLoading(true);

      debounceTimerRef.current = setTimeout(() => {
        if (!autocompleteServiceRef.current || !sessionTokenRef.current) {
          setIsLoading(false);
          return;
        }

        autocompleteServiceRef.current.getPlacePredictions(
          {
            input,
            sessionToken: sessionTokenRef.current,
            ...(countryRestrictions?.length && {
              componentRestrictions: { country: countryRestrictions },
            }),
          },
          (results, status) => {
            setIsLoading(false);
            if (status === google.maps.places.PlacesServiceStatus.OK && results) {
              setPredictions(results);
              setIsOpen(true);
            } else {
              setPredictions([]);
              setIsOpen(false);
            }
          },
        );
      }, 300);
    },
    [countryRestrictions],
  );

  const handleSelect = useCallback(
    (prediction: google.maps.places.AutocompletePrediction) => {
      if (!placesServiceRef.current || !places) return;

      placesServiceRef.current.getDetails(
        {
          placeId: prediction.place_id,
          fields: ["name", "formatted_address", "address_components", "geometry"],
          sessionToken: sessionTokenRef.current!,
        },
        (place, status) => {
          if (status === google.maps.places.PlacesServiceStatus.OK && place) {
            const display = place.formatted_address || prediction.description;
            setInputValue(display);
            onInputChange?.(display);
            onPlaceSelect(place);
          }
          setIsOpen(false);
          setPredictions([]);
          setHighlightedIndex(-1);
          // Reset session token for the next search session (billing optimization)
          sessionTokenRef.current = new places.AutocompleteSessionToken();
        },
      );
    },
    [onPlaceSelect, onInputChange, places],
  );

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    onInputChange?.(newValue);
    setHighlightedIndex(-1);
    fetchPredictions(newValue);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen || predictions.length === 0) return;

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setHighlightedIndex((prev) => (prev < predictions.length - 1 ? prev + 1 : 0));
        break;
      case "ArrowUp":
        e.preventDefault();
        setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : predictions.length - 1));
        break;
      case "Enter":
        e.preventDefault();
        if (highlightedIndex >= 0 && highlightedIndex < predictions.length) {
          handleSelect(predictions[highlightedIndex]);
        }
        break;
      case "Escape":
        setIsOpen(false);
        setHighlightedIndex(-1);
        break;
    }
  };

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
        setHighlightedIndex(-1);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const hasValue = inputValue !== "" && inputValue != null;

  return (
    <div className={cn("relative flex flex-col gap-2", wrapperClassName)}>
      <div
        className={cn(
          "relative flex h-14 w-full items-center rounded-lg border bg-white px-4 transition-all duration-200",
          {
            "border-blue-600": isFocused,
            "border-red-500": error,
            "border-gray-300": !isFocused && !error,
            "pointer-events-none bg-gray-50": disabled,
            "hover:border-gray-400": !disabled && !isFocused && !error,
          },
          className,
        )}
        onClick={() => {
          if (!disabled) inputRef.current?.focus();
        }}
      >
        {label && (
          <Label
            className={cn(
              "pointer-events-none absolute text-sm text-gray-500 transition-all duration-200 ease-in-out",
              {
                "-translate-y-3 text-xs": hasValue || isFocused,
                "translate-y-0 text-sm": !hasValue && !isFocused,
              },
            )}
          >
            {label}
            {required && <RequiredMark />}
          </Label>
        )}

        <input
          ref={inputRef}
          className={cn(
            "peer h-full w-full border-0 bg-transparent p-0 text-sm shadow-none outline-none focus:ring-0",
            label && (hasValue || isFocused) && "translate-y-1.5",
            disabled && "cursor-not-allowed",
          )}
          value={inputValue}
          onChange={handleInputChange}
          onFocus={() => {
            setIsFocused(true);
            if (inputValue.trim() && predictions.length > 0) setIsOpen(true);
          }}
          onBlur={() => setIsFocused(false)}
          onKeyDown={handleKeyDown}
          placeholder={!label ? placeholder : undefined}
          disabled={disabled}
          autoComplete="new-password"
          role="combobox"
          aria-expanded={isOpen}
          aria-haspopup="listbox"
          aria-autocomplete="list"
        />

        {isLoading && <Loader2 className="ml-2 h-4 w-4 shrink-0 animate-spin text-gray-400" />}
      </div>

      {isOpen && predictions.length > 0 && (
        <div
          ref={dropdownRef}
          className="absolute top-full left-0 z-50 mt-1 w-full overflow-hidden rounded-md border bg-white shadow-md"
          role="listbox"
        >
          <ul className="max-h-[300px] overflow-y-auto p-1">
            {predictions.map((prediction, index) => (
              <li
                key={prediction.place_id}
                role="option"
                aria-selected={highlightedIndex === index}
                className={cn(
                  "flex cursor-pointer items-center gap-2 rounded-sm px-3 py-2 text-sm transition-colors",
                  highlightedIndex === index
                    ? "bg-accent text-accent-foreground"
                    : "hover:bg-accent/50",
                )}
                onMouseEnter={() => setHighlightedIndex(index)}
                onMouseDown={(e) => {
                  e.preventDefault();
                  handleSelect(prediction);
                }}
              >
                <MapPin className="h-4 w-4 shrink-0 text-gray-400" />
                <div className="flex flex-col">
                  <span className="font-medium">{prediction.structured_formatting.main_text}</span>
                  <span className="text-xs text-gray-500">
                    {prediction.structured_formatting.secondary_text}
                  </span>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {error && <p className="text-xs text-red-500">{error}</p>}
    </div>
  );
};
